import { useCallback, useEffect, useRef, useState } from 'react';

type RefreshOptions = {
	queueIfInFlight?: boolean;
};

type ContributorsRefreshResult = {
	error?: Error;
	refreshNow: (options?: RefreshOptions) => Promise<void>;
};

// Refresh only while this tab is visible, keeping the last result on screen between requests.
export const useContributorsRefresh = (
	refresh: () => Promise<unknown>,
	{ refreshOnMount = true }: { refreshOnMount?: boolean } = {},
): ContributorsRefreshResult => {
	const [error, setError] = useState<Error>();
	const refreshRef = useRef<ContributorsRefreshResult['refreshNow'] | undefined>(undefined);
	const refreshNow = useCallback(
		(options?: RefreshOptions) => refreshRef.current?.(options) ?? Promise.resolve(),
		[],
	);

	useEffect(() => {
		setError(undefined);

		let disposed = false;
		let inFlight = false;
		let refreshQueued = false;

		const refreshContributors = async ({ queueIfInFlight = false }: RefreshOptions = {}) => {
			if (disposed || document.hidden) {
				return;
			}
			if (inFlight) {
				// A publish may arrive after the active request read the previous version.
				refreshQueued = refreshQueued || queueIfInFlight;
				return;
			}

			inFlight = true;
			try {
				do {
					refreshQueued = false;
					try {
						await refresh();
						if (!disposed) {
							setError(undefined);
						}
					} catch (error) {
						if (!disposed) {
							setError(
								error instanceof Error ? error : new Error('Failed to refresh contributors'),
							);
						}
					}
				} while (refreshQueued && !disposed && !document.hidden);
			} finally {
				inFlight = false;
			}
		};

		const onVisibilityChange = () => {
			if (!document.hidden) {
				// Catch changes missed while hidden, even if an older request is still in flight.
				void refreshContributors({ queueIfInFlight: true });
			}
		};

		// All refresh triggers share the same in-flight guard.
		refreshRef.current = refreshContributors;
		document.addEventListener('visibilitychange', onVisibilityChange);
		// Returning to a page reads a warm Relay store, which can hold contributors from before a
		// publish that happened while the reader was elsewhere. Refresh on mount to correct it.
		if (refreshOnMount) {
			void refreshContributors();
		}

		return () => {
			disposed = true;
			refreshRef.current = undefined;
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, [refresh, refreshOnMount]);

	return { error, refreshNow };
};
